import * as React from 'react'
import { cn } from '@/lib/utils'

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 0 a 100. Por debajo de 0 o por encima de 100 se recorta: una barra al 130 % miente. */
  value?: number
  /** Sin valor conocido: barra en movimiento. Es honesto decir "está pasando algo". */
  indeterminado?: boolean
  tono?: 'primary' | 'success' | 'warning' | 'danger'
}

const TONOS: Record<NonNullable<ProgressProps['tono']>, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
}

/**
 * Barra de avance.
 *
 * Existe porque una carga de 40.000 filas tarda minutos y el asesor está delante del
 * cliente: sin una barra, la única lectura posible es "se colgó". Con `indeterminado` para
 * las esperas cuyo final no se puede calcular — que es mejor que fingir un porcentaje.
 *
 * Lleva los `aria-*` del patrón `progressbar` porque un asesor con lector de pantalla
 * también necesita saber si la carga va por la mitad.
 */
export function Progress({ value = 0, indeterminado = false, tono = 'primary', className, ...props }: ProgressProps) {
  const pct = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      {...(indeterminado ? {} : { 'aria-valuenow': Math.round(pct) })}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800', className)}
      {...props}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-300 ease-out',
          TONOS[tono],
          indeterminado && 'w-1/3 animate-progress-indeterminado'
        )}
        style={indeterminado ? undefined : { width: `${pct}%` }}
      />
    </div>
  )
}

export default Progress
